import { payoffCellKey, type PayoffAxesConfig } from "./payoff_matrix_logic"

function normalizeLabelKey(label: string): string {
  return label.trim().toLowerCase()
}

function escapeCsvField(value: string): string {
  if (value.includes(",") || value.includes("\n") || value.includes('"')) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

/** Parse one CSV record line (handles quoted commas). */
export function parseCsvLine(line: string): string[] {
  const result: string[] = []
  let current = ""
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"'
        i += 1
      } else {
        inQuotes = !inQuotes
      }
    } else if (char === "," && !inQuotes) {
      result.push(current)
      current = ""
    } else {
      current += char
    }
  }
  result.push(current)
  return result
}

function parseCsvRows(text: string): string[][] {
  const normalized = text.replace(/\r\n/g, "\n").replace(/\r/g, "\n").trim()
  if (!normalized) {
    return []
  }
  return normalized.split("\n").map(line => parseCsvLine(line))
}

/**
 * Matrix CSV layout:
 * - Row 1: empty corner cell, then B axis labels as column headers
 * - Following rows: A axis label, then payoff values for each B column
 */
export function parsePayoffMatrixCsv(csvText: string, axes: PayoffAxesConfig): Record<string, string> {
  const rows = parseCsvRows(csvText)
  if (rows.length < 2 || rows[0].length < 2) {
    return {}
  }

  const header = rows[0]
  const bLabels = header.slice(1).map(cell => normalizeLabelKey(cell))

  const updates: Record<string, string> = {}

  for (let rowIndex = 1; rowIndex < rows.length; rowIndex++) {
    const row = rows[rowIndex]
    if (!row.length) {
      continue
    }

    const aEntry = axes.a.find(entry => normalizeLabelKey(entry.label) === normalizeLabelKey(row[0]))
    if (!aEntry) {
      continue
    }

    for (let colIndex = 0; colIndex < bLabels.length; colIndex++) {
      const bEntry = axes.b.find(entry => normalizeLabelKey(entry.label) === bLabels[colIndex])
      if (!bEntry) {
        continue
      }
      const raw = row[colIndex + 1]
      if (raw === undefined) {
        continue
      }
      const trimmed = raw.trim()
      if (trimmed === "") {
        continue
      }
      updates[payoffCellKey(aEntry.value, bEntry.value)] = trimmed
    }
  }

  return updates
}

export function buildPayoffMatrixCsv(
  axes: PayoffAxesConfig,
  getValue: (key: string) => string
): string {
  const header = ["", ...axes.b.map(entry => escapeCsvField(entry.label))]
  const body = axes.a.map(aEntry => {
    const cells = axes.b.map(bEntry => {
      const raw = getValue(payoffCellKey(aEntry.value, bEntry.value)).trim()
      return escapeCsvField(raw)
    })
    return [escapeCsvField(aEntry.label), ...cells].join(",")
  })
  return [header.join(","), ...body].join("\n")
}

export function downloadPayoffMatrixCsv(
  axes: PayoffAxesConfig,
  getValue: (key: string) => string,
  filename: string
): void {
  const csvContent = buildPayoffMatrixCsv(axes, getValue)
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement("a")
  anchor.href = url
  anchor.download = filename.endsWith(".csv") ? filename : `${filename}.csv`
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}
